import React, { useEffect, useState, useMemo } from "react";
import HomeIcon from "@mui/icons-material/Home";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import "./WealthSeriesPortfolioPage.scss";
import WealthSeriesPortfolioInfo from "./components/WealthSeriesPortfolioInfo/WealthSeriesPortfolioInfo";
import WealthSeriesPortfolioPerformanceGraph from "./components/WealthSeriesPortfolioPerformanceGraph/WealthSeriesPortfolioPerformanceGraph";
import WealthSeriesPortfolioTickers from "./components/WealthSeriesPortfolioTickers/WealthSeriesPortfolioTickers";
import WealthSeriesPortfolioKeyAdjustments from "./components/WealthSeriesPortfolioKeyAdjustments/WealthSeriesPortfolioKeyAdjustments";
import WealthSeriesPortfolioTickerInfo from "./components/WealthSeriesPortfolioTickerInfo/WealthSeriesPortfolioTickerInfo";
import { connect } from "react-redux";
import moment from "moment";
import BeautifulSkeleton from "../common/Skeletons/BeautifulSkeleton";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { useMedia } from "react-media";
import { portfolioSymbolSetter } from "../../appRedux/ducks/portfolioTicker";
import "moment-timezone";
import { useAuth } from "../../contexts/Auth";
import { portfolioHashMap } from "./components/WealthSeriesPortfolioTickers/tickerData";
import { portfolioLoad } from "../../appRedux/ducks/wealthPortfolio";
import AutoTradingAlert from "../common/AutoTradingAlert/AutoTradingAlert";

import { urlToNameMap } from "../WealthSeriesOverview/portfoliosData";
const mediaQuery = {
  isMobile: "screen and (max-width: 991px)",
};

const WealthSeriesPortfolioPage = ({
  wealthPortfolios,
  loadingPortfolio,
  setTicker,
  portfolioTicker,
  fetchPortfolioData,
}) => {
  const { series, portfolio } = useParams();
  const {
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
    userDataLoading,
    userData,
  } = useAuth();

  // Create state variables needed early in the component
  // Track if we've attempted to fetch the portfolio data
  const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);

  // Helper function to determine if auto portfolio should be used
  const shouldUseAutoPortfolio = useMemo(() => {
    return (
      (series === "atlas" && hasWealthSeriesAtlasAuto) ||
      (series === "titan" && hasWealthSeriesTitanAuto) ||
      (series === "everest" && hasWealthSeriesEverestAuto)
    );
  }, [
    series,
    hasWealthSeriesAtlasAuto,
    hasWealthSeriesTitanAuto,
    hasWealthSeriesEverestAuto,
  ]);

  // Define portfolio baseName before it's used in calculations
  const portfolioBaseName = useMemo(() => {
    return portfolio.replace("_auto", "");
  }, [portfolio]);

  // Get the appropriate portfolio ID
  const portfolioId = useMemo(() => {
    if (shouldUseAutoPortfolio) {
      const autoPortfolioName = `${portfolioBaseName}_auto`;
      const autoId = portfolioHashMap[autoPortfolioName];
      if (autoId) return autoId;
    }
    return portfolioHashMap[portfolioBaseName];
  }, [portfolioBaseName, shouldUseAutoPortfolio]);

  // Get portfolio data intelligently
  const portfolioData = useMemo(() => {
    // Try auto portfolio first if available and should be used
    if (shouldUseAutoPortfolio) {
      const autoPortfolioName = `${portfolioBaseName}_auto`;
      if (wealthPortfolios[autoPortfolioName]) {
        return wealthPortfolios[autoPortfolioName];
      }
    }

    // Fall back to regular portfolio
    if (wealthPortfolios[portfolioBaseName]) {
      return wealthPortfolios[portfolioBaseName];
    }

    // Return undefined if neither is found
    return undefined;
  }, [wealthPortfolios, portfolioBaseName, shouldUseAutoPortfolio]);

  // Fetch portfolio data on initial load and refresh
  useEffect(() => {
    if (userDataLoading || !userData) return;

    // Only fetch if portfolio data is not already available and not already loading
    const isAutoVariantAvailable =
      wealthPortfolios[`${portfolioBaseName}_auto`];
    const isRegularVariantAvailable = wealthPortfolios[portfolioBaseName];

    // If we already have the data we need, don't fetch
    if (
      (shouldUseAutoPortfolio && isAutoVariantAvailable) ||
      (!shouldUseAutoPortfolio && isRegularVariantAvailable)
    ) {
      return;
    }

    // Skip only if we're loading AND we've already attempted a fetch
    // This allows the initial fetch to go through even when loading starts as true
    if (loadingPortfolio && hasAttemptedFetch) return;

    // At this point, we need to fetch the right portfolio
    if (portfolioId) {
      setHasAttemptedFetch(true);
      fetchPortfolioData(portfolioId, true);
    }
  }, [
    portfolioId,
    userDataLoading,
    fetchPortfolioData,
    shouldUseAutoPortfolio,
    wealthPortfolios,
    portfolioBaseName,
    loadingPortfolio,
    userData,
    hasAttemptedFetch,
  ]);

  // Check if we should show the auto trading indicators
  const showAutoPortfolioIndicators = useMemo(() => {
    if (!shouldUseAutoPortfolio) return false;

    // Check if auto portfolio data is actually loaded
    const autoPortfolioName = `${portfolioBaseName}_auto`;
    return !!wealthPortfolios[autoPortfolioName];
  }, [shouldUseAutoPortfolio, portfolioBaseName, wealthPortfolios]);

  const lastUpdated = useMemo(() => {
    if (!portfolioData?.last_updated) {
      return "Loading...";
    }
    return `Last updated: ${moment(portfolioData.last_updated)
      .tz("America/New_York")
      .format("dddd, MMM D, YYYY h:mm A")} (EST)`;
  }, [portfolioData]);

  const history = useHistory();
  const [tabValue, setTabValue] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const ticker = queryParams.get("ticker");
  const [selectedTickerInfo, setSelectedTickerInfo] = useState({
    ticker: "",
    reason: "",
    isBuy: false,
    date: "",
  });
  const [timeframe, setTimeframe] = useState("1y");
  const [isViewingPastDate, setIsViewingPastDate] = useState(false);

  const { isMobile } = useMedia({
    queries: mediaQuery,
  });

  // Reference to scroll container
  const contentContainerRef = React.useRef(null);

  // Function to scroll to ticker details section on mobile
  const scrollToTickerDetails = () => {
    if (isMobile && portfolioTicker) {
      setTimeout(() => {
        // Try to find the ticker details container first
        const tickerDetailsEl = document.querySelector(
          ".ws-portfolio-ticker-info",
        );
        if (tickerDetailsEl) {
          window.scrollTo({
            top: tickerDetailsEl.offsetTop - 20, // Subtract some pixels to give space
            behavior: "smooth",
          });
        } else {
          // Fallback to the container
          const containerEl = document.querySelector(
            ".ws-portfolio-content-container__top",
          );
          if (containerEl) {
            window.scrollTo({
              top: containerEl.offsetTop,
              behavior: "smooth",
            });
          }
        }
      }, 300); // Increased timeout to ensure components are rendered
    }
  };

  useEffect(() => {
    // Scroll to ticker details when the portfolioTicker changes
    if (portfolioTicker) {
      scrollToTickerDetails();
    }
  }, [portfolioTicker, isMobile]);

  useEffect(() => {
    // Elements we care about
    const backgroundEl = document.querySelector(".site-layout-background");
    const containerEl = document.querySelector(
      ".ws-portfolio-content-container",
    );
    const fixedContentEl = document.querySelector(
      ".ws-portfolio-content-fixed",
    );

    if (!backgroundEl || !containerEl || !fixedContentEl) return;

    const handleWheel = (e) => {
      // Check if the scroll event is happening inside `.ws-portfolio-content-container` or `.ws-portfolio-content-fixed`
      const isInContainer =
        containerEl.contains(e.target) || fixedContentEl.contains(e.target);

      if (isInContainer) {
        // If the user is scrolling inside the container or its children, or inside the fixed content or its children,
        // let them scroll normally. Do nothing.
        return;
      } else {
        // Otherwise, prevent the default scrolling on `.site-layout-background`
        // and manually scroll `.ws-portfolio-content-container` instead.
        e.preventDefault();
        containerEl.scrollTop += e.deltaY;
      }
    };

    backgroundEl.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      backgroundEl.removeEventListener("wheel", handleWheel, {
        passive: false,
      });
    };
  }, []);

  useEffect(() => {
    if (ticker) {
      setTicker(ticker);
    }
    return () => setTicker("");
  }, []);

  return (
    <div className="ws-portfolio">
      <AutoTradingAlert
        showAutoPortfolioIndicators={showAutoPortfolioIndicators}
        broker="Orion"
      />

      <div className="ws-portfolio-header">
        <div className="ws-portfolio-header__breadcrumbs">
          <div
            onClick={() => history.push(`/wealth-series/${series}`)}
            className="ws-portfolio-header__breadcrumb-item"
          >
            <HomeIcon />
            <span>
              {series.charAt(0).toUpperCase() + series.slice(1)} Portfolios
            </span>
          </div>
          <span className="ws-portfolio-header__breadcrumb-separator">/</span>
          <div
            id="portfolio-name-breadcrumb"
            onClick={() => {
              if (isViewingPastDate) {
                setIsViewingPastDate(false);
              }
              history.push(
                `/wealth-series/${series}/${portfolio}${location.search}`,
              );
            }}
            className="ws-portfolio-header__breadcrumb-item"
          >
            <FormatListBulletedIcon />
            <span>{urlToNameMap[portfolioBaseName]}</span>
          </div>
          {isViewingPastDate && (
            <>
              <span className="ws-portfolio-header__breadcrumb-separator">
                /
              </span>
              <div className="ws-portfolio-header__breadcrumb-item non-clickable">
                <FormatListBulletedIcon />
                <span>Past Adjustment</span>
              </div>
            </>
          )}
          {portfolioTicker && (
            <>
              <span className="ws-portfolio-header__breadcrumb-separator">
                /
              </span>
              <div className="ws-portfolio-header__breadcrumb-item">
                <ShowChartIcon />
                <span>{portfolioTicker}</span>
              </div>
            </>
          )}
        </div>
        {!isMobile && (
          <div className="ws-portfolio-header__timestamp">
            {loadingPortfolio ? (
              <BeautifulSkeleton width={350} height={20} />
            ) : (
              lastUpdated
            )}
          </div>
        )}
      </div>

      <div className="ws-portfolio-content">
        <div className="ws-portfolio-content-fixed">
          <WealthSeriesPortfolioInfo
            tabValue={tabValue}
            setTabValue={setTabValue}
            isMobile={isMobile}
            lastUpdated={lastUpdated}
            portfolioData={portfolioData}
            shouldFetch={false}
          />
          <div className="ws-portfolio-content-body ws-scroll-element">
            <WealthSeriesPortfolioPerformanceGraph
              tabValue={tabValue}
              timeframe={timeframe}
              setTimeframe={setTimeframe}
              portfolioData={portfolioData}
            />
            <WealthSeriesPortfolioTickers
              setSelectedTickerInfo={setSelectedTickerInfo}
              timeframe={timeframe}
              portfolioData={portfolioData}
              isViewingPastDate={isViewingPastDate}
              setIsViewingPastDate={setIsViewingPastDate}
            />
          </div>
        </div>

        <div
          className="ws-portfolio-content-container"
          ref={contentContainerRef}
        >
          {portfolioTicker ? (
            <WealthSeriesPortfolioTickerInfo
              selectedTickerInfo={selectedTickerInfo}
              ticker={portfolioTicker}
              portfolioData={portfolioData}
            />
          ) : (
            <WealthSeriesPortfolioKeyAdjustments
              selectedTickerInfo={selectedTickerInfo}
              setSelectedTickerInfo={setSelectedTickerInfo}
              setIsViewingPastDate={setIsViewingPastDate}
              isMobile={isMobile}
              portfolioData={portfolioData}
            />
          )}
        </div>
      </div>

      <div className="ws-portfolio-disclaimer">
        This website provides AI-powered insights for informational purposes
        only. Please use at your own discretion, and consult a qualified
        financial advisor for personalized guidance.
      </div>
    </div>
  );
};

const stateToProps = (state) => ({
  portfolioTicker: state.portfolioTicker.symbol,
  wealthPortfolios: state.wealthPortfolio,
  loadingPortfolio: state.wealthPortfolio.loading,
});

const dispatchToProps = (dispatch) => ({
  setTicker: (symbol) => dispatch(portfolioSymbolSetter(symbol)),
  fetchPortfolioData: (id, addSparkline = true) =>
    dispatch(portfolioLoad(id, addSparkline)),
});

export default connect(
  stateToProps,
  dispatchToProps,
)(WealthSeriesPortfolioPage);
